import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Filter, ArrowRight, ZoomIn, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import ReviewCarousel from '../components/ReviewCarousel';
import GalleryModal from '../components/GalleryModal';
import { jobById, jobPath, jobs } from '../data/jobs';
import SEO from '../components/SEO';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [hoveredId, setHoveredId] = useState(null);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async () => {
    try {
      const response = await axios.get(`${API}/portfolio`);
      setPortfolioItems(response.data);
    } catch (error) {
      console.error('Error fetching portfolio:', error);
    } finally {
      setLoading(false);
    }
  };

  const openGallery = (item) => {
    // Combine cover image with any additional gallery images
    const allImages = item.images && item.images.length > 0 
      ? [item.image, ...item.images.filter(img => img !== item.image)]
      : [item.image];
    
    setSelectedProject({
      ...item,
      allImages
    });
    setGalleryOpen(true);
  };

  // Short description of one photo, where the project has one.
  const captionFor = (item, src) =>
    (item.captions || []).find((c) => c.image === src)?.text;

  const categories = ['All', ...new Set(portfolioItems.map(item => item.category))];

  const filteredItems = selectedCategory === 'All' 
    ? portfolioItems 
    : portfolioItems.filter(item => item.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-cyan-50">
      <SEO 
        title="Recent Painting & Decorating Projects in Kent | Paint It Better"
        description="Browse our portfolio of painting and decorating projects across Kent. Interior, exterior, period properties and rental refreshes. See the quality of our work."
        canonical="/portfolio"
      />
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-cyan-600 to-teal-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Our Recent Projects in Kent
            </h1>
            <p className="text-xl text-cyan-100">
              Explore our painting and decorating projects across Wye, Ashford, Canterbury, Faversham and surrounding Kent villages.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 bg-white sticky top-0 z-10 shadow-md">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Filter className="h-5 w-5 text-slate-600" />
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => setSelectedCategory(category)}
                variant={selectedCategory === category ? 'default' : 'outline'}
                className={`rounded-full transition-all duration-300 ${
                  selectedCategory === category 
                    ? 'bg-cyan-600 hover:bg-cyan-700 text-white shadow-lg scale-105' 
                    : 'hover:bg-cyan-50 hover:text-cyan-600 hover:border-cyan-600'
                }`}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
              <p className="mt-4 text-slate-600">Loading portfolio...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-slate-600">No portfolio items yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredItems.map((item, index) => (
              <Card 
                key={item.id}
                className="group overflow-hidden border-0 bg-white hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer"
                onMouseEnter={() => setHoveredId(item.id)}
                onMouseLeave={() => setHoveredId(null)}
                onClick={() => openGallery(item)}
                style={{
                  animationDelay: `${index * 50}ms`
                }}
              >
                <div className="relative h-80 overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={captionFor(item, item.image) || `${item.title} – ${item.category} project by Paint It Better in Kent`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent transition-opacity duration-300 ${
                    hoveredId === item.id ? 'opacity-100' : 'opacity-60'
                  }`} />
                  
                  {/* View Gallery Icon */}
                  <div className={`absolute top-4 left-4 bg-white/90 text-slate-900 rounded-full p-2 transition-all duration-300 ${
                    hoveredId === item.id ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                  }`}>
                    <ZoomIn className="h-5 w-5" />
                  </div>
                  
                  <div className="absolute top-4 right-4">
                    <span className="bg-cyan-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                      {item.category}
                    </span>
                  </div>

                  <div className={`absolute bottom-0 left-0 right-0 p-6 transition-all duration-300 transform ${
                    hoveredId === item.id ? 'translate-y-0' : 'translate-y-2'
                  }`}>
                    <h3 className="text-2xl font-bold text-white mb-2">{item.title}</h3>
                    {item.location && (
                      <p className="flex items-center gap-1 text-cyan-200 text-sm mb-2">
                        <MapPin className="h-4 w-4" /> {item.location}
                      </p>
                    )}
                    <p className={`text-cyan-100 transition-all duration-300 ${
                      hoveredId === item.id ? 'opacity-100 max-h-20' : 'opacity-0 max-h-0'
                    }`}>
                      {item.description}
                    </p>
                    {item.images && item.images.length > 0 && (
                      <p className={`text-cyan-200 text-sm mt-2 transition-all duration-300 ${
                        hoveredId === item.id ? 'opacity-100' : 'opacity-0'
                      }`}>
                        Click to view {item.images.length + 1} photos
                      </p>
                    )}
                    {jobById(item.id) && (
                      <Link
                        to={jobPath(jobById(item.id))}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-block text-white underline underline-offset-2 text-sm mt-2"
                      >
                        Read about this job
                      </Link>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* Job write-ups: plain links, so they are in the page search engines read */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">Recent jobs, written up</h2>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {jobs.map((j) => (
              <li key={j.slug}>
                <Link to={jobPath(j)} className="inline-flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-medium">
                  {j.title} <ArrowRight className="h-4 w-4" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Checkatrade Portfolio Link */}
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-lg text-slate-700 mb-4">
              <strong>Looking for more?</strong> We have additional project photos and detailed customer feedback on{' '}
              <a 
                href="https://www.checkatrade.com/trades/PaintItBetterLtd" 
                target="_blank" 
                rel="noopener"
                className="text-cyan-600 hover:text-cyan-700 font-semibold underline decoration-2 underline-offset-2"
              >
                our Checkatrade profile
              </a>
              . See verified reviews from Kent homeowners who've experienced our painting and decorating services.
            </p>
            <a 
              href="https://www.checkatrade.com/trades/PaintItBetterLtd" 
              target="_blank" 
              rel="noopener"
              className="inline-flex items-center text-cyan-600 hover:text-cyan-700 font-medium transition-colors"
            >
              See more projects on Checkatrade
              <ArrowRight className="ml-1 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-600 to-teal-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Like What You See?
            </h2>
            <p className="text-xl mb-8 text-cyan-100">
              Let's create something beautiful together. Get in touch for a free consultation.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-cyan-600 hover:bg-cyan-50 px-8 py-6 text-lg rounded-full transition-all duration-300 hover:scale-105 shadow-xl">
                Start Your Project
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Review Carousel */}
      <ReviewCarousel />

      {/* Gallery Modal */}
      {selectedProject && (
        <GalleryModal
          isOpen={galleryOpen}
          onClose={() => {
            setGalleryOpen(false);
            setSelectedProject(null);
          }}
          images={selectedProject.allImages}
          captions={selectedProject.allImages.map((src) => captionFor(selectedProject, src))}
          title={selectedProject.location ? `${selectedProject.title}, ${selectedProject.location}` : selectedProject.title}
        />
      )}
    </div>
  );
};

export default Portfolio;